import React, { useEffect, useState } from "react";
import { api } from "../utils/api";

function useScanWallet() {
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");

  const reload = async () => {
    const res = await api("/nyafaz-ep/licenses/credits");
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || "Scan limit is unavailable");
    setWallet(data);
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/nyafaz-ep/licenses/credits");
        const data = await res.json();
        if (cancelled) return;
        if (res.ok) setWallet(data);
        else setError(data.error || data.message || "Scan limit is unavailable");
      } catch {
        if (!cancelled) setError("Scan limit is unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { wallet, error, setError, reload };
}

export function ScanLimitHeaderChip() {
  const { wallet, error } = useScanWallet();
  const remaining = wallet?.remaining;
  const total = (wallet?.allowance || 0) + (wallet?.topup || 0);
  const percent = Math.min(Number(wallet?.percentUsed || 0), 100);
  const tone =
    remaining != null && total > 0 && remaining <= 0
      ? "is-exhausted"
      : percent >= 80
        ? "is-low"
        : "";

  const label =
    error ||
    (remaining != null && total > 0
      ? `${remaining} / ${total} scans left`
      : remaining != null
        ? `${remaining} scans left`
        : "Scan balance…");

  return (
    <a
      href="/nyafaz-ep/partner/ib-dashboard#scan-purchase"
      className={`scan-limit-header-chip${tone ? ` ${tone}` : ""}`}
      title={error || limitText(wallet)}
    >
      <span className="scan-limit-header-chip__label">Scans</span>
      <span className="scan-limit-header-chip__value">{label}</span>
      {wallet?.remaining != null && total > 0 ? (
        <span className="scan-limit-header-chip__bar" aria-hidden>
          <span className="scan-limit-header-chip__fill" style={{ width: `${percent}%` }} />
        </span>
      ) : null}
    </a>
  );
}

function limitText(wallet) {
  if (!wallet || wallet.remaining == null) return "Scan balance loads with the active Analyze and Studio plan.";
  const total = (wallet.allowance || 0) + (wallet.topup || 0);
  return `${wallet.remaining} scans left · ${wallet.used || 0} used of ${total} (${wallet.percentUsed || 0}%)`;
}

export function ScanLimitBanner() {
  const { wallet, error } = useScanWallet();
  const total = (wallet?.allowance || 0) + (wallet?.topup || 0);
  const percent = Math.min(Number(wallet?.percentUsed || 0), 100);
  return (
    <a href="#scan-purchase" className="block rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold">Scan limit</span>
        <span className="text-white/80">{error || limitText(wallet)}</span>
      </div>
      {wallet?.remaining != null ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15">
          <div className="h-full rounded-full bg-[#8cf6ff]" style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {total ? <p className="mt-2 text-xs text-white/55">Plan {wallet.allowance || 0} · extra {wallet.topup || 0}</p> : null}
    </a>
  );
}

export default function ScanLimitPurchase() {
  const { wallet, error, setError, reload } = useScanWallet();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const total = (wallet?.allowance || 0) + (wallet?.topup || 0);
  const percent = Math.min(Number(wallet?.percentUsed || 0), 100);

  const buy = async () => {
    setBusy(true);
    setError("");
    setLink("");
    try {
      const res = await api("/nyafaz-ep/licenses/credits/purchase", {
        method: "POST",
        body: JSON.stringify({ code: "scans_10" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Could not create the payment link");
      setLink(data.shortUrl || "");
      await reload();
    } catch (err) {
      setError(err.message || "Could not create the payment link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 text-white">
      <p className="text-sm text-white/80">{error || limitText(wallet)}</p>
      {wallet?.remaining != null ? (
        <div className="h-2 overflow-hidden rounded-full bg-white/15">
          <div className="h-full rounded-full bg-[#8cf6ff]" style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {total ? (
        <p className="text-xs text-white/55">
          Plan scans {wallet.allowance || 0} · extra scans {wallet.topup || 0}
        </p>
      ) : null}
      <p className="text-sm text-white/70">
        Buy 10 additional scans for ₹100 plus GST. They are added after payment and unused packs carry to the next billing
        period. Plan scans reset each period.
      </p>
      {link ? (
        <a className="block text-sm text-[#8cf6ff] underline" href={link} target="_blank" rel="noreferrer">
          {link}
        </a>
      ) : null}
      <button
        type="button"
        className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#1f1630] disabled:opacity-60"
        disabled={busy}
        onClick={buy}
      >
        {busy ? "Creating link…" : "Buy 10 scans"}
      </button>
    </div>
  );
}
